import type { CSSProperties, ReactNode } from 'react';
import { Badge, Surface, type BadgeVariant } from '@/interfaces/learner/ui';

interface ActionBadge {
  key: string;
  label: ReactNode;
  variant?: BadgeVariant;
}

interface CourseActionPanelProps {
  eyebrow: string;
  title: string;
  description: string;
  badges?: ActionBadge[];
  header?: ReactNode;
  tone?: 'accent' | 'neutral';
  children?: ReactNode;
  style?: CSSProperties;
}

export default function CourseActionPanel({
  eyebrow,
  title,
  description,
  badges = [],
  header,
  tone = 'neutral',
  children,
  style,
}: CourseActionPanelProps) {
  return (
    <Surface
      tone="solid"
      padding={22}
      style={{
        background: tone === 'accent' ? 'var(--color-accent-wash-strong)' : undefined,
        ...style,
      }}
    >
      <p className="text-eyebrow" style={{ color: 'var(--color-accent-text)' }}>
        {eyebrow}
      </p>

      {header ? (
        <div className="mt-3">{header}</div>
      ) : (
        <div className="mt-3">
          <p className="text-panel-title">{title}</p>
          <p className="text-meta mt-2">{description}</p>
        </div>
      )}

      {badges.length > 0 && (
        <div className="mt-4 flex flex-wrap gap-2.5">
          {badges.map((badge) => (
            <Badge key={badge.key} variant={badge.variant ?? 'muted'}>
              {badge.label}
            </Badge>
          ))}
        </div>
      )}

      {children ? <div className="mt-5">{children}</div> : null}
    </Surface>
  );
}
